/**
 * Observer API Client
 * Provides HTTP client methods for observer endpoints
 */

import { 
  AiThreadDetail, 
  Message, 
  ObserverStats, 
  ThreadSearchResult, 
  UserSearchResult 
} from '../store/observerTypes';
import HttpClient from '../../apis/httpClient';

// Base API configuration
const API_BASE_URL = '/api/observer';

export interface ObserverChatMessagePayload {
  message_id: string | number;
  ai_thread_id: string | number;
  user_id: string | null;
  content: string;
  content_type?: string;
  created_at: string;
  human_review?: any;
  human_review_status?: string;
  human_review_response?: string;
  human_review_ack?: string | { content: string; user_id?: string | null; user_name?: string | null };
  agent_display_name?: string | null;
  isHistory?: boolean;
}

export interface LlmUsageSummary {
  attempt_count: number;
  input_tokens: number | null;
  output_tokens: number | null;
  total_tokens: number | null;
  input_cached_tokens: number | null;
  input_cache_write_tokens: number | null;
  total_cost: number | null;
  missing_usage_count: number;
  unpriced_count: number;
}

export interface ThreadTurnUsage extends LlmUsageSummary {
  turn_id: string;
  turn_index: number;
  feature: string | null;
  start_time: string | null;
  failed_attempts: number;
  duration_ms: number;
  elapsed_ms: number | null;
}

export interface ThreadUsage extends LlmUsageSummary {
  missing_turn_count: number;
  thread_id: string;
  turns: ThreadTurnUsage[];
}

export interface RagObserverResult {
  source: 'kb_rag' | 'ticket_rag';
  title: string;
  content: string;
  url: string;
  source_doc_type?: string;
  source_doc_id?: string;
  ticket_id?: string;
  created_at?: string;
  distance?: string;
  workflow_id?: string;
}

export type IngestionSourceDocType = 'guide' | 'kb' | 'hudu_articles' | 'itglue_docs' | 'psa_tickets';

export interface ObserverIngestionPlatform {
  id: number;
  name: string;
  type: string;
}

export interface ObserverIngestionStatusResponse {
  source_doc_type: IngestionSourceDocType;
  isRunning: boolean;
  ingestion: {
    id: number;
    msp_id: number;
    source_doc_type: IngestionSourceDocType;
    status: string;
    created_at: string;
    completed_at: string | null;
  } | null;
}

export interface ObserverIngestionTriggerResponse {
  success: boolean;
  alreadyRunning: boolean;
  message: string;
  source_doc_type: IngestionSourceDocType;
  ingestion: {
    id: number;
    msp_id: number;
    source_doc_type: IngestionSourceDocType;
    status: string;
    created_at: string;
    completed_at: string | null;
  } | null;
}

/** Activity Report (Observer tab); matches server `activityReportService` response shape. */
export interface ActivityReportAuditError {
  tool_name: string | null;
  status: string | null;
  timestamp: string | null;
  reasoning: string | null;
  error_message: string | null;
  output_error: string | null;
}

export interface ActivityReportThreadAnalysis {
  m365_integration: boolean;
  gworkspace_integration?: boolean;
  /** e.g. ["M365"], ["GWorkspace"], or both. Empty when neither is enabled. */
  productivity_integrations?: string[];
  /** Display: "No integration" | "M365" | "GWorkspace" | "M365 · GWorkspace". */
  productivity_integration_label?: string;
  customer_in_theo?: boolean;
  m365_customer_id: number | null;
  m365_customer_name: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  summary: string;
  expectation_assessment: string;
  potential_improvements: string[];
  audit_errors: ActivityReportAuditError[];
}

export interface ActivityReportTopThread {
  usage: LlmUsageSummary;
  ai_thread_id: number;
  ticket_id: string | null;
  customer_id: number | null;
  research_enabled?: boolean | number | string | null;
  thread_type: 'ticket_assistant' | 'deep_research' | 'chat_assistant';
  thread_name: string | null;
  msgs_on_day: number;
  last_message_at: string | null;
  /** Last non-neo message author, else thread `user_id` (Observer display_user_id). */
  user_email: string | null;
  analysis: ActivityReportThreadAnalysis;
}

export interface ActivityReportMspSection {
  usage: LlmUsageSummary;
  mspId: number;
  mspName: string;
  threadCount: number;
  threadsWithTicket: number;
  threadsAssistantOnly: number;
  messageCount: number;
  distinctHumanUsers: number;
  toolActions: number;
  topThreads: ActivityReportTopThread[];
}

export interface ActivityReportResponse {
  usage: LlmUsageSummary;
  /** Inclusive UTC calendar start (YYYY-MM-DD). */
  startDateUtc: string;
  /** Inclusive UTC calendar end (YYYY-MM-DD). */
  endDateUtc: string;
  sections: ActivityReportMspSection[];
}

/** Usage Quality Observer tab — matches theoUsageQualityService payload. */
export interface TheoUsageQualityThreadLabels {
  primary_issue_type: string;
  user_intent_summary: string;
  theo_helpfulness: string;
  gap_category: string;
  gap_detail: string;
  features_used: string[];
  evidence: string;
  how_theo_helped: string;
  investigation_quality?: string | null;
  would_need_human?: boolean;
}

export interface TheoUsageQualityThread {
  ai_thread_id: number;
  msp_id: number;
  msp_name: string;
  customer_id?: number | null;
  customer_name?: string | null;
  /** True when Horizon has a customer row for this thread (ticket or customer_id). */
  customer_in_theo?: boolean;
  /** True when that customer has enabled M365 application_config. */
  m365_integration?: boolean;
  gworkspace_integration?: boolean;
  /** e.g. ["M365"], ["GWorkspace"], or both. */
  productivity_integrations?: string[];
  /** Display: "No integration" | "M365" | "GWorkspace" | "M365 · GWorkspace". */
  productivity_integration_label?: string;
  ticket_id: string | null;
  thread_name: string | null;
  /** Last non-neo/autoagent message author, else thread owner. */
  user_email?: string | null;
  research_enabled?: boolean | number | string | null;
  track: 'interactive' | 'autoagent';
  last_message_at: string | null;
  labels: TheoUsageQualityThreadLabels;
  cache?: string;
}

/** One LLM gap-narrative bullet with optional PSA ticket / thread evidence. */
export interface TheoUsageGapNarrativeItem {
  summary: string;
  ticket_ids?: string[];
  thread_ids?: number[];
}

export interface TheoUsageGapNarrativeSections {
  gaps: TheoUsageGapNarrativeItem[];
  missing_capabilities: TheoUsageGapNarrativeItem[];
  enhancements: TheoUsageGapNarrativeItem[];
  strengths_to_preserve: TheoUsageGapNarrativeItem[];
}

export interface TheoUsageGapNarrativeResponse {
  sections?: TheoUsageGapNarrativeSections;
  /** Flattened summaries (legacy / convenience). */
  bullets: string[];
  meta: {
    model: string;
    cache: string;
    threadCount: number;
    redisOk?: boolean;
    generated_at?: string;
    integration_context?: {
      total: number;
      m365: number;
      gworkspace: number;
      no_integration: number;
      not_in_theo: number;
    };
  };
}

export interface TheoUsageQualityResponse {
  report: {
    startDateUtc: string;
    endDateUtc: string;
    includeAutoagent: boolean;
    threads: TheoUsageQualityThread[];
    hierarchy: {
      interactive: Record<string, Record<string, Record<string, {
        count: number;
        gaps: Record<string, number>;
        samples: Array<{
          ai_thread_id: number;
          ticket_id: string | null;
          thread_name: string | null;
          intent?: string;
          how_theo_helped?: string;
          features_used?: string[];
          gap?: string;
          gap_detail?: string;
        }>;
      }>>>;
      autoagent: Record<string, Record<string, Record<string, unknown>>> | null;
    };
    rollups: {
      interactive: {
        helpfulness: Record<string, number>;
        issue_types: Record<string, number>;
        gaps: Record<string, number>;
      };
      autoagent: {
        helpfulness: Record<string, number>;
        issue_types: Record<string, number>;
        investigation_quality: Record<string, number>;
      } | null;
    };
  };
  summary: {
    generated_at?: string;
    startDateUtc?: string;
    endDateUtc?: string;
    include_autoagent?: boolean;
    interactive_count?: number;
    autoagent_count?: number;
    interactive?: {
      helpfulness?: Record<string, number>;
      issue_types?: Record<string, number>;
      gaps?: Record<string, number>;
      by_msp?: Record<
        string,
        {
          count: number;
          helpfulness: Record<string, number>;
          issue_types: Record<string, number>;
          gaps: Record<string, number>;
        }
      >;
    };
    autoagent?: Record<string, unknown> | null;
  };
  summaryHtml: string;
  meta: {
    spanDays: number;
    maxLookbackDays: number;
    threadCandidates: number;
    cacheHits: number;
    cacheMisses: number;
    model: string;
    cache: string;
    redisOk?: boolean;
  };
}

class ObserverApi extends HttpClient {
  protected url;

  constructor() {
    super();
    this.url = API_BASE_URL;
  }

  /**
   * Get threads with filtering and pagination
   */
  getThreads = async (params: {
    page?: number;
    pageSize?: number;
    mspId?: number;
    userId?: string;
    ticketId?: string;
    isDeleted?: boolean | null;
    startDate?: string;
    endDate?: string;
    minMessages?: number | null;
    maxMessages?: number | null;
    sortBy?: 'created' | 'updated' | 'messageCount';
    sortOrder?: 'ASC' | 'DESC';
  } = {}): Promise<ThreadSearchResult> => {
    const searchParams = new URLSearchParams();
    
    // Add non-null parameters to search params
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `${API_BASE_URL}/threads?${queryString}` : `${API_BASE_URL}/threads`;
    
    return this.get(endpoint);
  };

  /**
   * Get detailed thread information with messages
   */
  getThreadDetail = async (threadId: number): Promise<{
    thread: AiThreadDetail;
    messages: Message[];
    chat_messages: ObserverChatMessagePayload[];
  }> => {
    return this.get(`${API_BASE_URL}/threads/${threadId}`);
  };

  /**
   * Get recorded usage details for a thread
   */
  getThreadUsage = async (threadId: number): Promise<ThreadUsage> => {
    return this.get(`${API_BASE_URL}/threads/${threadId}/usage`);
  };

  /**
   * Get observer statistics
   */
  getObserverStats = async (): Promise<ObserverStats> => {
    return this.get(`${API_BASE_URL}/stats`);
  };

  /**
   * Search for users (typeahead)
   */
  searchUsers = async (query: string, limit: number = 10): Promise<UserSearchResult> => {
    const searchParams = new URLSearchParams({
      q: query,
      limit: String(limit),
    });
    
    return this.get(`${API_BASE_URL}/search/users?${searchParams.toString()}`);
  };

  /**
   * Soft delete a thread
   */
  deleteThread = async (threadId: number): Promise<{ success: boolean; message: string }> => {
    return this.del(`${API_BASE_URL}/threads/${threadId}`);
  };

  /**
   * Export thread data as JSON
   */
  exportThreadAsJson = async (threadId: number): Promise<string> => {
    const { thread, messages } = await this.getThreadDetail(threadId);
    
    const exportData = {
      thread,
      messages,
      exportedAt: new Date().toISOString(),
      exportFormat: 'json',
    };
    
    return JSON.stringify(exportData, null, 2);
  };

  /**
   * Export thread data as CSV
   */
  exportThreadAsCsv = async (threadId: number): Promise<string> => {
    const { thread, messages } = await this.getThreadDetail(threadId);
    
    // CSV header
    const csvRows = [
      'Thread ID,User ID,Ticket ID,Thread Name,Message ID,Content Type,Content,Created At',
    ];
    
    // Add thread info to each message row
    messages.forEach(message => {
      const row = [
        thread.ai_thread_id,
        thread.user_id || '',
        thread.ticket_id || '',
        thread.thread_name || '',
        message.message_id,
        message.content_type,
        `"${message.content.replace(/"/g, '""')}"`, // Escape quotes in content
        message.created_at,
      ];
      csvRows.push(row.join(','));
    });
    
    return csvRows.join('\n');
  };

  /**
   * Copy text to clipboard (client-side utility)
   */
  copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  };

  /**
   * Get users for the selected Observer MSP scope (Horizon users + thread/message actors).
   */
  getUsers = async (
    mspIds: number[],
    omitInternalUsers = false,
    omitAutoagent = false,
  ): Promise<Array<{
    id: string;
    email: string;
  }>> => {
    const searchParams = new URLSearchParams();
    searchParams.append('mspIds', mspIds.join(','));
    searchParams.append('omitInternalUsers', String(omitInternalUsers));
    searchParams.append('omitAutoagent', String(omitAutoagent));

    const endpoint = `${API_BASE_URL}/users?${searchParams.toString()}`;

    const result = await this.get(endpoint);
    return result.users;
  };

  /**
   * Get all MSPs from horizondb
   */
  getMSPs = async (): Promise<Array<{
    id: number;
    name: string;
  }>> => {
    const result = await this.get(`${API_BASE_URL}/msps`);
    return result.msps;
  };

  /**
   * Get threads for MSP(s), optionally filtered by user(s).
   */
  getUserThreads = async (params: {
    userIds?: string[] | null;
    mspIds: number[];
    startDate?: string;
    endDate?: string;
    isDeleted?: boolean;
  }): Promise<{
    threads: Array<{
      usage: LlmUsageSummary;
      ai_thread_id: number;
      thread_user_id: string | null;
      display_user_id: string | null;
      ticket_id: string | null;
      thread_name: string | null;
      msp_id?: number | null;
      pinned: boolean;
      is_deleted: boolean;
      created_at: string;
      updated_at: string | null;
      message_count: number;
      last_message_at: string | null;
      first_message_at: string | null;
    }>;
    totalCount: number;
    page: number;
    pageSize: number;
  }> => {
    const searchParams = new URLSearchParams();

    searchParams.append('mspIds', params.mspIds.join(','));
    if (params.userIds === null) {
      searchParams.append('userIds', '');
    } else if (params.userIds && params.userIds.length > 0) {
      searchParams.append('userIds', params.userIds.join(','));
    }
    if (params.startDate) {
      searchParams.append('startDate', params.startDate);
    }
    if (params.endDate) {
      searchParams.append('endDate', params.endDate);
    }
    if (params.isDeleted != null) {
      searchParams.append('isDeleted', String(params.isDeleted));
    }

    const queryString = searchParams.toString();
    const endpoint = `${API_BASE_URL}/user-threads?${queryString}`;

    return this.get(endpoint);
  };

  /**
   * Run debug RAG query in cortex through observer route.
   */
  queryRag = async (params: {
    query: string;
    source: 'kb_rag' | 'ticket_rag';
    mspId: number;
    /** kb_rag: Horizon customers.id. ticket_rag: PSA company id (past_tickets.client_id). */
    clientId?: string;
    docType?: string;
    ticketStatuses?: string;
    excludeTicketId?: string;
    /** ticket_rag: ISO date or datetime (UTC), matches Cortex `created_after` */
    ticketCreatedAfter?: string;
    /** ticket_rag: ISO date or datetime (UTC), matches Cortex `created_before` */
    ticketCreatedBefore?: string;
    topK?: number;
    /** ticket_rag: "PROBLEM_SUMMARY" | "RESOLUTION_SUMMARY"; omit for both */
    ticketEmbeddingKind?: string;
    /** ticket_rag: priority filter (e.g. low); normalized to lowercase server-side */
    ticketPriority?: string;
  }): Promise<{ source: 'kb_rag' | 'ticket_rag'; results: RagObserverResult[] }> => {
    // HttpClient.post(url, headersConfig, body) — third arg is JSON body (same pattern as authApi/chatApi).
    return this.post(`${API_BASE_URL}/rag/query`, {}, params);
  };

  /**
   * Preview or execute KB RAG cleanup (kb_documents + Vertex kb_* vectors).
   * Omit mspId to scope all MSPs. Cleanup filters only — not RAG query fields.
   */
  cleanupKbRag = async (params: {
    mspId?: number;
    execute?: boolean;
    clientId?: string;
    sourceDocType?: string;
    sourceDocId?: string;
  }): Promise<{
    executeMode: boolean;
    matchedCount: number;
    vertexDeleteOk: number;
    vertexDeleteFailed: number;
    mysqlDeleted: number;
    kbDocumentIds: number[];
    errors: string[];
  }> => {
    const body: Record<string, unknown> = { execute: params.execute ?? false };
    if (params.mspId != null && Number.isFinite(params.mspId)) {
      body.mspId = params.mspId;
    }
    if (params.clientId != null && String(params.clientId).trim() !== '') {
      body.clientId = String(params.clientId).trim();
    }
    if (params.sourceDocType != null && String(params.sourceDocType).trim() !== '') {
      body.sourceDocType = String(params.sourceDocType).trim();
    }
    if (params.sourceDocId != null && String(params.sourceDocId).trim() !== '') {
      body.sourceDocId = String(params.sourceDocId).trim();
    }
    return this.post(`${API_BASE_URL}/rag/cleanup`, {}, body);
  };

  /**
   * Preview or execute ticket RAG cleanup (past_tickets + Vertex ticket_* vectors).
   * Same index config as KB/ticket ingestion (shared kb_rag index). Omit mspId for all MSPs.
   * Optional clientId is the PSA company id (past_tickets.client_id), not Horizon customers.id.
   */
  cleanupTicketRag = async (params: {
    mspId?: number;
    execute?: boolean;
    /** PSA company id filter (past_tickets.client_id). */
    clientId?: string;
    ticketId?: string;
    ticketType?: string;
  }): Promise<{
    executeMode: boolean;
    matchedCount: number;
    vertexDeleteOk: number;
    vertexDeleteFailed: number;
    mysqlDeleted: number;
    pastTicketIds: number[];
    errors: string[];
  }> => {
    const body: Record<string, unknown> = { execute: params.execute ?? false };
    if (params.mspId != null && Number.isFinite(params.mspId)) {
      body.mspId = params.mspId;
    }
    if (params.clientId != null && String(params.clientId).trim() !== '') {
      body.clientId = String(params.clientId).trim();
    }
    if (params.ticketId != null && String(params.ticketId).trim() !== '') {
      body.ticketId = String(params.ticketId).trim();
    }
    if (params.ticketType != null && String(params.ticketType).trim() !== '') {
      body.ticketType = String(params.ticketType).trim();
    }
    return this.post(`${API_BASE_URL}/rag/cleanup-ticket`, {}, body);
  };

  getIngestionStatus = async (
    mspId: number,
    sourceDocType: IngestionSourceDocType,
    platformId?: number,
  ): Promise<ObserverIngestionStatusResponse> => {
    const params: Record<string, unknown> = {
      mspId,
      source_doc_type: sourceDocType,
    };
    if (platformId != null && Number.isFinite(platformId)) {
      params.platform_id = platformId;
    }
    return this.get(`${API_BASE_URL}/rag/ingestion/status`, {}, params);
  };

  triggerIngestion = async (
    mspId: number,
    sourceDocType: IngestionSourceDocType,
    platformId?: number,
  ): Promise<ObserverIngestionTriggerResponse> => {
    const body: Record<string, unknown> = {
      mspId,
      source_doc_type: sourceDocType,
    };
    if (platformId != null && Number.isFinite(platformId)) {
      body.platform_id = platformId;
    }
    return this.post(`${API_BASE_URL}/rag/ingestion/trigger`, {}, body);
  };

  getIngestionPlatforms = async (
    mspId: number,
    type: 'psa' | 'docs' = 'psa',
  ): Promise<ObserverIngestionPlatform[]> => {
    const result = await this.get(`${API_BASE_URL}/rag/ingestion/platforms`, {}, { mspId, type });
    return result?.platforms || [];
  };

  /**
   * Activity Report (inclusive UTC date range). Defaults to today UTC on the server when omitted.
   * Optional legacy `date` sets a single day (start = end = date).
   */
  getActivityReport = async (params: {
    startDate?: string;
    endDate?: string;
    date?: string;
    mspIds: number[];
    userIds?: string[] | null;
  }): Promise<ActivityReportResponse> => {
    const searchParams = new URLSearchParams();
    if (params.startDate) {
      searchParams.append('startDate', params.startDate);
    }
    if (params.endDate) {
      searchParams.append('endDate', params.endDate);
    }
    if (params.date) {
      searchParams.append('date', params.date);
    }
    searchParams.append('mspIds', params.mspIds.join(','));
    if (params.userIds === null) {
      searchParams.append('userIds', '');
    } else if (params.userIds && params.userIds.length > 0) {
      searchParams.append('userIds', params.userIds.join(','));
    }
    const qs = searchParams.toString();
    const base = `${API_BASE_URL}/activity-report`;
    const endpoint = qs ? `${base}?${qs}` : base;
    return this.get(endpoint);
  };

  /**
   * Theo Usage / Quality report (ROOT Observer tab).
   * Max lookback 30 days (server-enforced). Always excludes internal users.
   */
  postTheoUsageQuality = async (params: {
    startDate: string;
    endDate: string;
    mspIds: number[];
    includeAutoagent?: boolean;
    forceRefresh?: boolean;
  }): Promise<TheoUsageQualityResponse> => {
    return this.post(`${API_BASE_URL}/theo-usage-quality`, {}, {
      startDate: params.startDate,
      endDate: params.endDate,
      mspIds: params.mspIds,
      includeAutoagent: Boolean(params.includeAutoagent),
      forceRefresh: Boolean(params.forceRefresh),
    });
  };

  /**
   * Summary-tab LLM sectioned narrative (Redis 30d by sorted thread ids).
   * Pass ticket + productivity integration fields so the model does not treat
   * expected M365 absences (no customer M365) as product capability gaps.
   */
  postTheoUsageGapNarrative = async (params: {
    threads: Array<{
      ai_thread_id: number;
      ticket_id?: string | null;
      msp_name?: string | null;
      customer_name?: string | null;
      customer_in_theo?: boolean;
      m365_integration?: boolean;
      gworkspace_integration?: boolean;
      productivity_integration_label?: string | null;
      labels?: Partial<TheoUsageQualityThreadLabels> | null;
    }>;
    forceRefresh?: boolean;
  }): Promise<TheoUsageGapNarrativeResponse> => {
    // LLM can take >60s on large windows; HttpClient default timeout is 1m.
    return this.post(
      `${API_BASE_URL}/theo-usage-quality/gap-narrative`,
      { timeout: 180000 },
      {
        threads: params.threads,
        forceRefresh: Boolean(params.forceRefresh),
      },
    );
  };
}

const observerApi = new ObserverApi();

export const getThreads = observerApi.getThreads.bind(observerApi);
export const getThreadDetail = observerApi.getThreadDetail.bind(observerApi);
export const getThreadUsage = observerApi.getThreadUsage.bind(observerApi);
export const getObserverStats = observerApi.getObserverStats.bind(observerApi);
export const searchUsers = observerApi.searchUsers.bind(observerApi);
export const deleteThread = observerApi.deleteThread.bind(observerApi);
export const exportThreadAsJson = observerApi.exportThreadAsJson.bind(observerApi);
export const exportThreadAsCsv = observerApi.exportThreadAsCsv.bind(observerApi);
export const copyToClipboard = observerApi.copyToClipboard.bind(observerApi);
export const getUsers = observerApi.getUsers.bind(observerApi);
export const getMSPs = observerApi.getMSPs.bind(observerApi);
export const getUserThreads = observerApi.getUserThreads.bind(observerApi);

export default ObserverApi;
